import React from 'react';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TabList } from '@/components/ui/navigation/TabList';
import { Text } from '@/components/ui/text/Text';
import {
  CreateMonthlyCutGeneralForm,
  CreateMonthlyCutGroupsForm,
} from '@/components/features/monthlyCuts';
import { useEditMonthlyCut } from '@/hooks/attendance/monthlyCuts';
import type { MonthlyCutTabId } from '@/hooks/attendance/monthlyCuts/useCreateMonthlyCut';
import type { EditMonthlyCutFormData } from '@/schemas/attendance/editMonthlyCutForm';
import { useTranslation } from '@/i18n';


export const MonthlyCutEditPage: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    permissionsLoaded,
    canEdit,
    screenCode,
    isLoading,
    isSubmitting,
    isClosed,
    loadFailed,
    activeTab,
    setActiveTab,
    form,
    cutId,
    companyName,
    applyToAllCompanies,
    yearOptions,
    monthOptions,
    groups,
    handleCancel,
    handleUpdate,
  } = useEditMonthlyCut();

  if (permissionsLoaded && !canEdit) return null;

  // Corte cerrado o no cargable: bloquear el formulario y mostrar el mensaje correspondiente.
  if (!isLoading && (isClosed || loadFailed)) {
    const message = isClosed
      ? translate('attendance:monthlyCutEdit.messages.closedNotEditable')
      : translate('attendance:monthlyCutEdit.messages.loadError');
    return (
      <PageLayout dynamicTitle={translate('attendance:monthlyCutEdit.title')}>
        <div
          className="flex flex-col items-center gap-6 py-12"
          data-testid="monthly-cuts-edit-page"
        >
          <Text variant="body-regular" data-testid="monthly-cut-edit-blocked-message">
            {message}
          </Text>
          <PrimaryButton
            variant="outline"
            onClick={handleCancel}
            data-testid="monthly-cut-cancel-button"
          >
            {translate('common:actions.back')}
          </PrimaryButton>
        </div>
      </PageLayout>
    );
  }

  const groupsErrorMessage = form.errors.groupIds?.message
    ? translate(form.errors.groupIds.message)
    : undefined;

  const tabs = [
    {
      id: 'general',
      label: translate('attendance:monthlyCutEdit.tabs.general'),
      content: (
        <CreateMonthlyCutGeneralForm<EditMonthlyCutFormData>
          form={form}
          mode="edit"
          cutId={cutId}
          companyName={companyName}
          applyToAllCompanies={applyToAllCompanies}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
        />
      ),
    },
    {
      id: 'agrupacion',
      label: translate('attendance:monthlyCutEdit.tabs.groups'),
      content: (
        <CreateMonthlyCutGroupsForm groups={groups} errorMessage={groupsErrorMessage} />
      ),
    },
  ];

  return (
    <PageLayout isLoading={isLoading} dynamicTitle={translate('attendance:monthlyCutEdit.title')}>
      <div className="flex flex-col gap-6" data-testid="monthly-cuts-edit-page">
        <TabList
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as MonthlyCutTabId)}
          keepMounted
        />

        <div className="flex justify-center gap-3 pt-2">
          <PrimaryButton
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            data-testid="monthly-cut-cancel-button"
          >
            {translate('common:actions.cancel')}
          </PrimaryButton>
          <PermissionGuard action="edit" screenCode={screenCode}>
            <PrimaryButton
              onClick={handleUpdate}
              isLoading={isSubmitting}
              disabled={isSubmitting}
              data-testid="monthly-cut-save-button"
            >
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        </div>
      </div>
    </PageLayout>
  );
};

export default MonthlyCutEditPage;
